import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Plus, FileText, Send, Trash2, Check, X, Building2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { useAdminAuth } from "../../context/AdminAuthContext";

const EMPTY_ITEM = { description: "", quantity: 1, unit_price_excl: 0 };

const EMPTY_FORM = {
  recipient_company: "",
  recipient_contact: "",
  recipient_email: "",
  recipient_kvk: "",
  recipient_btw: "",
  recipient_address_line_1: "",
  recipient_address_line_2: "",
  recipient_country: "Nederland",
  reference: "",
  notes: "",
  payment_terms_days: 14,
  vat_rate: 21,
  shipping_cost: 0,
  items: [{ ...EMPTY_ITEM }],
};

const STATUS_LABEL = {
  draft: { label: "CONCEPT", color: "#9ca3af" },
  sent: { label: "VERZONDEN", color: "#facc15" },
  paid: { label: "BETAALD", color: "#22c55e" },
  cancelled: { label: "GEANNULEERD", color: "#6b7280" },
};

const AdminB2BInvoices = () => {
  const { authHeaders } = useOutletContext();
  const { token } = useAdminAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/b2b-invoices`, { headers: authHeaders })
      .then((r) => setInvoices(r.data || []))
      .catch(() => toast.error("Facturen laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const create = async (form) => {
    setCreating(true);
    try {
      const payload = {
        ...form,
        payment_terms_days: parseInt(form.payment_terms_days, 10) || 14,
        vat_rate: parseFloat(form.vat_rate) || 21,
        shipping_cost: parseFloat(form.shipping_cost) || 0,
        items: form.items.map((it) => ({
          description: it.description.trim(),
          quantity: parseFloat(it.quantity) || 0,
          unit_price_excl: parseFloat(it.unit_price_excl) || 0,
        })),
      };
      // Strip empty optional strings to null
      ["recipient_contact", "recipient_email", "recipient_kvk", "recipient_btw",
       "recipient_address_line_1", "recipient_address_line_2", "reference", "notes"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await axios.post(`${API}/admin/b2b-invoices`, payload, { headers: authHeaders });
      toast.success("Factuur aangemaakt");
      setShowForm(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Aanmaken mislukt";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/admin/b2b-invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `factuur-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF ophalen mislukt");
    }
  };

  const openPdf = (inv) => {
    const url = `${API}/admin/b2b-invoices/${inv.id}/pdf?token=${token}`;
    // We need auth header — easier: fetch as blob and open
    axios
      .get(`${API}/admin/b2b-invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      })
      .catch(() => toast.error("PDF openen mislukt"));
  };

  const sendInvoice = async (inv) => {
    if (!inv.recipient_email) {
      toast.error("Geen e-mailadres bij deze factuur");
      return;
    }
    if (!window.confirm(`Factuur ${inv.invoice_number} versturen naar ${inv.recipient_email}?`)) return;
    setActingId(inv.id);
    try {
      await axios.post(`${API}/admin/b2b-invoices/${inv.id}/send`, {}, { headers: authHeaders });
      toast.success(`Factuur verzonden naar ${inv.recipient_email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    } finally {
      setActingId(null);
    }
  };

  const regenerate = async (inv) => {
    setActingId(inv.id);
    try {
      await axios.post(`${API}/admin/b2b-invoices/${inv.id}/regenerate`, {}, { headers: authHeaders });
      toast.success("PDF opnieuw gegenereerd met huidige instellingen");
      load();
    } catch {
      toast.error("Hergenereren mislukt");
    } finally {
      setActingId(null);
    }
  };

  const markPaid = async (inv) => {
    setActingId(inv.id);
    try {
      await axios.patch(
        `${API}/admin/b2b-invoices/${inv.id}`,
        { status: "paid" },
        { headers: authHeaders }
      );
      toast.success("Gemarkeerd als betaald");
      load();
    } catch {
      toast.error("Bijwerken mislukt");
    } finally {
      setActingId(null);
    }
  };

  const markDraft = async (inv) => {
    setActingId(inv.id);
    try {
      await axios.patch(
        `${API}/admin/b2b-invoices/${inv.id}`,
        { status: "draft" },
        { headers: authHeaders }
      );
      load();
    } catch {
      toast.error("Bijwerken mislukt");
    } finally {
      setActingId(null);
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Factuur ${inv.invoice_number} permanent verwijderen?`)) return;
    setActingId(inv.id);
    try {
      await axios.delete(`${API}/admin/b2b-invoices/${inv.id}`, { headers: authHeaders });
      toast.success("Factuur verwijderd");
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div data-testid="admin-b2b-invoices" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-display text-3xl">ZAKELIJKE FACTUREN</div>
          <div className="text-sm font-mono-x text-[var(--tha-muted)] mt-1">
            Maak manueel facturen aan voor zakelijke klanten (bv. VVV, horeca, evenementen).
          </div>
        </div>
        <button
          data-testid="b2b-new-invoice"
          onClick={() => setShowForm(true)}
          className="bg-[var(--tha-red)] text-black font-mono-x tracking-widest text-xs px-5 py-3 inline-flex items-center gap-2 hover:opacity-90"
        >
          <Plus size={14} /> NIEUWE FACTUUR
        </button>
      </div>

      {loading ? (
        <div className="text-[var(--tha-muted)] font-mono-x text-sm">Laden…</div>
      ) : invoices.length === 0 ? (
        <div className="border border-dashed border-[var(--tha-border)] p-10 text-center">
          <Building2 size={28} className="mx-auto text-[var(--tha-muted)] mb-3" />
          <div className="font-display text-2xl mb-2">NOG GEEN ZAKELIJKE FACTUREN</div>
          <div className="text-sm font-mono-x text-[var(--tha-muted)]">
            Klik op "NIEUWE FACTUUR" om je eerste zakelijke factuur aan te maken.
          </div>
        </div>
      ) : (
        <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] border-b border-[var(--tha-border)] bg-[#050505]">
            <div className="col-span-2">FACTUURNR</div>
            <div className="col-span-3">KLANT</div>
            <div className="col-span-2">DATUM</div>
            <div className="col-span-2 text-right">BEDRAG</div>
            <div className="col-span-1">STATUS</div>
            <div className="col-span-2 text-right">ACTIES</div>
          </div>
          {invoices.map((inv) => {
            const status = STATUS_LABEL[inv.status] || STATUS_LABEL.draft;
            const acting = actingId === inv.id;
            return (
              <div
                key={inv.id}
                data-testid={`b2b-invoice-row-${inv.invoice_number}`}
                className="grid grid-cols-12 gap-3 px-4 py-4 border-b border-[var(--tha-border)] last:border-0 items-center text-sm"
              >
                <div className="col-span-2 font-mono-x text-white">{inv.invoice_number}</div>
                <div className="col-span-3">
                  <div className="text-white">{inv.recipient_company}</div>
                  <div className="text-xs text-[var(--tha-muted)]">
                    {inv.recipient_email || "geen e-mail"}
                  </div>
                </div>
                <div className="col-span-2 font-mono-x text-[var(--tha-muted)] text-xs">
                  {(inv.created_at || "").slice(0, 10)}
                  <div className="text-[10px]">verloopt {(inv.due_date || "").slice(0, 10)}</div>
                </div>
                <div className="col-span-2 text-right font-display text-xl text-white">
                  €&nbsp;{(inv.total_incl || 0).toFixed(2)}
                </div>
                <div className="col-span-1">
                  <span
                    className="inline-block px-2 py-1 text-[10px] font-mono-x tracking-widest"
                    style={{ color: status.color, border: `1px solid ${status.color}66` }}
                  >
                    {status.label}
                  </span>
                </div>
                <div className="col-span-2 flex justify-end gap-1 flex-wrap">
                  <IconBtn
                    title="PDF bekijken"
                    onClick={() => openPdf(inv)}
                    testId={`b2b-pdf-${inv.invoice_number}`}
                  >
                    <FileText size={14} />
                  </IconBtn>
                  <IconBtn
                    title="PDF hergenereren met huidige instellingen"
                    onClick={() => regenerate(inv)}
                    disabled={acting}
                    testId={`b2b-regen-${inv.invoice_number}`}
                  >
                    <RefreshCw size={14} />
                  </IconBtn>
                  <IconBtn
                    title="Versturen naar klant"
                    onClick={() => sendInvoice(inv)}
                    disabled={acting || !inv.recipient_email}
                    accent
                    testId={`b2b-send-${inv.invoice_number}`}
                  >
                    <Send size={14} />
                  </IconBtn>
                  {inv.status !== "paid" ? (
                    <IconBtn
                      title="Markeren als betaald"
                      onClick={() => markPaid(inv)}
                      disabled={acting}
                      testId={`b2b-paid-${inv.invoice_number}`}
                    >
                      <Check size={14} />
                    </IconBtn>
                  ) : (
                    <IconBtn
                      title="Terug naar concept"
                      onClick={() => markDraft(inv)}
                      disabled={acting}
                      testId={`b2b-undo-paid-${inv.invoice_number}`}
                    >
                      <X size={14} />
                    </IconBtn>
                  )}
                  <IconBtn
                    title="Verwijderen"
                    onClick={() => deleteInvoice(inv)}
                    disabled={acting}
                    danger
                    testId={`b2b-delete-${inv.invoice_number}`}
                  >
                    <Trash2 size={14} />
                  </IconBtn>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <InvoiceFormModal
          onClose={() => setShowForm(false)}
          onSubmit={create}
          submitting={creating}
        />
      )}
    </div>
  );
};

const IconBtn = ({ children, onClick, title, disabled, accent, danger, testId }) => (
  <button
    title={title}
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`border px-2 py-2 transition-colors disabled:opacity-40 ${
      accent
        ? "border-[var(--tha-red)] text-[var(--tha-red)] hover:bg-[var(--tha-red)] hover:text-black"
        : danger
        ? "border-[var(--tha-border)] text-[var(--tha-muted)] hover:border-red-700 hover:text-red-500"
        : "border-[var(--tha-border)] text-[var(--tha-muted)] hover:text-white hover:border-white"
    }`}
  >
    {children}
  </button>
);

const InvoiceFormModal = ({ onClose, onSubmit, submitting }) => {
  const [form, setForm] = useState(EMPTY_FORM);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const updateItem = (i, k, v) =>
    setForm((f) => {
      const arr = [...f.items];
      arr[i] = { ...arr[i], [k]: v };
      return { ...f, items: arr };
    });
  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) =>
    setForm((f) => {
      if (f.items.length <= 1) return f;
      const arr = [...f.items];
      arr.splice(i, 1);
      return { ...f, items: arr };
    });

  const totals = useMemo(() => {
    const subtotal = form.items.reduce(
      (sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price_excl) || 0),
      0
    );
    const shipping = parseFloat(form.shipping_cost) || 0;
    const vatRate = parseFloat(form.vat_rate) || 0;
    const taxable = subtotal + shipping;
    const vat = taxable * (vatRate / 100);
    return {
      subtotal,
      shipping,
      vat,
      total: taxable + vat,
    };
  }, [form.items, form.vat_rate, form.shipping_cost]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.recipient_company.trim()) {
      toast.error("Bedrijfsnaam is verplicht");
      return;
    }
    const hasValidItem = form.items.some(
      (it) => it.description.trim() && parseFloat(it.quantity) > 0
    );
    if (!hasValidItem) {
      toast.error("Voeg minstens één geldige factuurregel toe");
      return;
    }
    onSubmit(form);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl bg-[#0a0a0a] border border-[var(--tha-border)] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--tha-border)] bg-[#050505]">
          <div>
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-red)]">NIEUWE B2B FACTUUR</div>
            <div className="font-display text-2xl mt-1">FACTUUR AANMAKEN</div>
          </div>
          <button onClick={onClose} className="text-[var(--tha-muted)] hover:text-white" data-testid="b2b-form-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          {/* Recipient */}
          <section>
            <SectionTitle>KLANTGEGEVENS</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Bedrijfsnaam *" testId="b2b-recipient-company">
                <input value={form.recipient_company} onChange={set("recipient_company")} className={inputCls} placeholder="VVV Kantoor Assen" data-testid="b2b-recipient-company-input" />
              </FormField>
              <FormField label="Contactpersoon">
                <input value={form.recipient_contact} onChange={set("recipient_contact")} className={inputCls} placeholder="Naam van de contactpersoon" />
              </FormField>
              <FormField label="E-mailadres (voor verzending)">
                <input type="email" value={form.recipient_email} onChange={set("recipient_email")} className={inputCls} placeholder="inkoop@bedrijf.nl" data-testid="b2b-recipient-email-input" />
              </FormField>
              <FormField label="Referentie / PO-nummer">
                <input value={form.reference} onChange={set("reference")} className={inputCls} placeholder="PO-2026-001" />
              </FormField>
              <FormField label="Adres">
                <input value={form.recipient_address_line_1} onChange={set("recipient_address_line_1")} className={inputCls} placeholder="Straat + huisnummer" />
              </FormField>
              <FormField label="Postcode + plaats">
                <input value={form.recipient_address_line_2} onChange={set("recipient_address_line_2")} className={inputCls} placeholder="1234 AB Plaats" />
              </FormField>
              <FormField label="KvK-nummer (optioneel)">
                <input value={form.recipient_kvk} onChange={set("recipient_kvk")} className={inputCls} placeholder="12345678" />
              </FormField>
              <FormField label="BTW-nummer (optioneel)">
                <input value={form.recipient_btw} onChange={set("recipient_btw")} className={inputCls} placeholder="NL123456789B01" />
              </FormField>
            </div>
          </section>

          {/* Items */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <SectionTitle className="!mb-0">FACTUURREGELS</SectionTitle>
              <button type="button" onClick={addItem} className="text-xs font-mono-x tracking-widest text-[var(--tha-red)] hover:text-white inline-flex items-center gap-1" data-testid="b2b-add-item">
                <Plus size={12} /> REGEL TOEVOEGEN
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] px-2">
                <div className="col-span-6">OMSCHRIJVING</div>
                <div className="col-span-2 text-right">AANTAL</div>
                <div className="col-span-3 text-right">PRIJS EXCL. BTW</div>
                <div className="col-span-1" />
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`b2b-item-row-${i}`}>
                  <input
                    value={it.description}
                    onChange={(e) => updateItem(i, "description", e.target.value)}
                    className={`${inputCls} col-span-6`}
                    placeholder='Bijv. "Red Rush — 6-pack"'
                    data-testid={`b2b-item-desc-${i}`}
                  />
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    className={`${inputCls} col-span-2 text-right`}
                    data-testid={`b2b-item-qty-${i}`}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.unit_price_excl}
                    onChange={(e) => updateItem(i, "unit_price_excl", e.target.value)}
                    className={`${inputCls} col-span-3 text-right`}
                    data-testid={`b2b-item-price-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    disabled={form.items.length === 1}
                    className="col-span-1 flex justify-center text-[var(--tha-muted)] hover:text-red-500 disabled:opacity-30"
                    data-testid={`b2b-item-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Live totals */}
            <div className="mt-4 border-t border-[var(--tha-border)] pt-4 flex justify-end">
              <div className="w-full sm:w-80 space-y-1 text-sm font-mono-x">
                <div className="flex justify-between text-[var(--tha-muted)]">
                  <span>Subtotaal (excl. BTW)</span>
                  <span data-testid="b2b-preview-subtotal">€ {totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.shipping > 0 && (
                  <div className="flex justify-between text-[var(--tha-muted)]">
                    <span>Verzendkosten (excl. BTW)</span>
                    <span data-testid="b2b-preview-shipping">€ {totals.shipping.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[var(--tha-muted)]">
                  <span>BTW {form.vat_rate}%</span>
                  <span data-testid="b2b-preview-vat">€ {totals.vat.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-white font-display text-2xl pt-2 border-t border-[var(--tha-border)]">
                  <span>TOTAAL</span>
                  <span data-testid="b2b-preview-total">€ {totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Settings */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <FormField label="Betaaltermijn (dagen)">
              <input type="number" min="1" max="180" value={form.payment_terms_days} onChange={set("payment_terms_days")} className={inputCls} />
            </FormField>
            <FormField label="BTW-tarief (%)">
              <input type="number" min="0" max="100" step="0.5" value={form.vat_rate} onChange={set("vat_rate")} className={inputCls} />
            </FormField>
            <FormField label="Verzendkosten (excl. BTW)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.shipping_cost}
                onChange={set("shipping_cost")}
                className={inputCls}
                placeholder="0.00"
                data-testid="b2b-shipping-input"
              />
            </FormField>
            <FormField label="Land">
              <input value={form.recipient_country} onChange={set("recipient_country")} className={inputCls} />
            </FormField>
          </section>

          <FormField label="Opmerkingen (verschijnt onderaan de factuur)">
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={3}
              className={inputCls}
              placeholder="Bijv. levering volgt na ontvangst betaling, of speciale afspraken."
            />
          </FormField>

          <div className="flex gap-3 pt-4 border-t border-[var(--tha-border)]">
            <button
              type="submit"
              disabled={submitting}
              className="bg-[var(--tha-red)] text-black font-mono-x tracking-widest text-xs px-6 py-3 hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="b2b-submit"
            >
              <FileText size={14} /> {submitting ? "AANMAKEN…" : "FACTUUR AANMAKEN"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border border-[var(--tha-border)] hover:border-white text-[var(--tha-muted)] hover:text-white font-mono-x tracking-widest text-xs px-6 py-3"
            >
              ANNULEREN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SectionTitle = ({ children, className = "" }) => (
  <div className={`text-[10px] font-mono-x tracking-widest text-[var(--tha-red)] mb-3 ${className}`}>
    {children}
  </div>
);

const FormField = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-[#050505] border border-[var(--tha-border)] px-3 py-2 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]";

export default AdminB2BInvoices;
